import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSignature, CheckCircle, AlertCircle } from 'lucide-react';

const NDASigning = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    ndaAccepted: initialData.ndaAccepted || false,
    signature: initialData.signature || '',
    signedDate: initialData.signedDate || '',
    ...initialData
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.ndaAccepted) {
      newErrors.ndaAccepted = t('validation.ndaAcceptance.required');
    }

    if (!formData.signature.trim()) {
      newErrors.signature = t('validation.signature.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNDAAcceptance = (accepted) => {
    const newFormData = {
      ...formData,
      ndaAccepted: accepted,
      signedDate: accepted ? new Date().toISOString().split('T')[0] : ''
    };
    setFormData(newFormData);
    
    if (errors.ndaAccepted) {
      setErrors(prev => ({
        ...prev,
        ndaAccepted: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileSignature className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('workflow.onboarding.ndaSigning.title')}
              </h2>
              <p className="text-gray-600 mt-1">
                {t('workflow.onboarding.ndaSigning.description')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* NDA Document Preview */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('workflow.onboarding.ndaSigning.documentTitle')}
            </h3>
            
            <div className="prose prose-sm max-w-none text-gray-700">
              <p className="mb-4">
                {t('workflow.onboarding.ndaSigning.documentContent.intro')}
              </p>
              
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('workflow.onboarding.ndaSigning.documentContent.confidentiality.title')}
              </h4>
              <p className="mb-4">
                {t('workflow.onboarding.ndaSigning.documentContent.confidentiality.text')}
              </p>
              
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('workflow.onboarding.ndaSigning.documentContent.obligations.title')}
              </h4>
              <p className="mb-4">
                {t('workflow.onboarding.ndaSigning.documentContent.obligations.text')}
              </p>
              
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('workflow.onboarding.ndaSigning.documentContent.term.title')}
              </h4>
              <p className="mb-4">
                {t('workflow.onboarding.ndaSigning.documentContent.term.text')}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* NDA Acceptance */}
            <div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    id="ndaAccepted"
                    type="checkbox"
                    checked={formData.ndaAccepted}
                    onChange={(e) => handleNDAAcceptance(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="text-sm">
                  <label htmlFor="ndaAccepted" className="font-medium text-gray-900">
                    {t('workflow.onboarding.ndaSigning.acceptanceLabel')}
                  </label>
                  <p className="text-gray-600 mt-1">
                    {t('workflow.onboarding.ndaSigning.acceptanceDescription')}
                  </p>
                </div>
              </div>
              {errors.ndaAccepted && (
                <div className="mt-2 flex items-center space-x-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <p className="text-sm">{errors.ndaAccepted}</p>
                </div>
              )}
            </div>

            {/* Digital Signature */}
            {formData.ndaAccepted && (
              <div>
                <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('workflow.onboarding.ndaSigning.signatureLabel')} *
                </label>
                <input
                  type="text"
                  id="signature"
                  value={formData.signature}
                  onChange={(e) => handleInputChange('signature', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.signature ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('workflow.onboarding.ndaSigning.signaturePlaceholder')}
                />
                {errors.signature && (
                  <p className="mt-1 text-sm text-red-600">{errors.signature}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  {t('workflow.onboarding.ndaSigning.signatureHelp')}
                </p>
              </div>
            )}

            {/* Signed Date Display */}
            {formData.ndaAccepted && formData.signedDate && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {t('workflow.onboarding.ndaSigning.signedOn')}
                    </p>
                    <p className="text-sm text-green-700">
                      {new Date(formData.signedDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={!formData.ndaAccepted}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t('common.continue')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NDASigning;
